import { Breakpoint, Breakpoints } from '@mui/material';
import { createContext, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { createStore, StoreApi } from 'zustand';
import { useTheme } from '../useTheme';
import {
  BreakpointMatches,
  BreakpointsStore,
  getFallbackBreakpointsStore,
  isMobileBreakpoint,
  setActiveBreakpoint,
} from './breakpoint-provider-model';
import { FALLBACK_BREAKPOINT, getFallbackMatchMedia, getSupportsMatchMedia } from './fallbackMatchMediaUtils';

export interface ViewportBreakpointProviderProps {
  children?: React.ReactNode;
}

/**
 * Mapping between breakpoint keys and the associated `matchMedia`-friendly media query strings to match them.
 *
 * @example
 * {
 *    xs: '(min-width:0px) and (max-width:599.95px)',
 *    sm: '(min-width:600px) and (max-width:899.95px)',
 *    md: '(min-width:900px) and (max-width:1199.95px)',
 *    lg: '(min-width:1200px) and (max-width:1535.95px)',
 *    xl: '(min-width:1536px)',
 * };
 */
type BreakpointToMediaQueryMap = Record<Breakpoint, string>;

/**
 * Mapping between breakpoint keys and the MediaQueryList objects that represent `matchMedia` queries for those objects.
 *
 *  @example
 * {
 *    xs: MediaQueryList,
 *    sm: MediaQueryList,
 *    md: MediaQueryList,
 *    lg: MediaQueryList,
 *    xl: MediaQueryList,
 * };
 */
type BreakpointToMediaQueryListMap = Record<Breakpoint, MediaQueryList>;

// Regex that will match the piece of the CSS-based media queries that won't work with `matchMedia`. Matches existing
// logic from MUI.
// https://github.com/mui/material-ui/blob/v5.x/packages/mui-system/src/useMediaQuery/useMediaQuery.ts#L178C25-L178C43
const REPLACE_MEDIA_REGEX = /^@media( ?)/m;

/**
 * Returns a mapping of breakpoints to media query strings that are compatible with the `matchMedia` api for those
 * breakpoints.
 */
export function getBreakpointToMediaQueryMap(breakpoints: Breakpoints): BreakpointToMediaQueryMap {
  return breakpoints.keys.reduce((result, breakpointKey, index) => {
    // Configure media queries for `matchMedia` to be exact, so there should only be one "true"
    // match at any given time. Note that the first breakpoint does not need special handling via a `down` (similar
    // to the `up` we do for the last breakpoint) because the first breakpoint is 0.
    let mediaQuery: string;
    if (index < breakpoints.keys.length - 1) {
      // We are sure that `nextBreakpointKey` is not undefined because of the conditional above, but TS does not
      // seem able to infer this, so adding a `!`.
      const nextBreakpointKey = breakpoints.keys[index + 1]!;
      mediaQuery = breakpoints.between(breakpointKey, nextBreakpointKey);
    } else {
      // Last breakpoint covers everything above that breakpoint.
      mediaQuery = breakpoints.up(breakpointKey);
    }

    return {
      ...result,
      // The media queries from MUI are returned as css-based selectors. We need to remove the @media prefix for
      // them to work with the `matchMedia` api.
      [breakpointKey]: mediaQuery.replace(REPLACE_MEDIA_REGEX, ''),
    };
  }, {} as BreakpointToMediaQueryMap);
}

/**
 * Returns a mapping of breakpoints to `matchMedia` objects that query for those breakpoints.
 *
 * When `matchMedia` is not supported (e.g. in jest tests, which use jsdom), they will instead map to a simple fallback
 * handler, so that the application does not break.
 */
export function getBreakpointToMediaQueryListMap(breakpoints: Breakpoints): BreakpointToMediaQueryListMap {
  const supportsMatchMedia = getSupportsMatchMedia();

  const breakpointToMediaQueryMap = getBreakpointToMediaQueryMap(breakpoints);

  return breakpoints.keys.reduce((result, breakpointKey) => {
    return {
      ...result,
      [breakpointKey]: supportsMatchMedia
        ? window.matchMedia(breakpointToMediaQueryMap[breakpointKey])
        : getFallbackMatchMedia(breakpointKey === FALLBACK_BREAKPOINT),
    };
  }, {} as BreakpointToMediaQueryListMap);
}

/**
 * Initializes the breakpoint store with the current state of the `matchMedia` objects.
 *
 * @example
 * // Example of initialized state for the store when the active breakpoint is `md`
 * {
 *    xs: false,
 *    sm: false,
 *    md: true,
 *    lg: false,
 *    xl: false,
 *    activeBreakpoint: 'md',
 *    isMobile: false,
 *    setActiveBreakpoint: Function
 * };
 */
function initializeBreakpointStore(
  breakpoints: Breakpoints,
  breakpointToMediaQueryMap: BreakpointToMediaQueryListMap
): StoreApi<BreakpointsStore> {
  return createStore<BreakpointsStore>()((set) => {
    // Initialize to fallback breakpoint to avoid a rare case where this value could end up undefined.
    let activeBreakpoint: Breakpoint = FALLBACK_BREAKPOINT;

    // Iterate through the map of breakpoint-based media queries to intialize the map of matches and determine what
    // the active breakpoint is.
    const breakpointMatches: BreakpointMatches = breakpoints.keys.reduce((result, breakpointKey) => {
      if (breakpointToMediaQueryMap[breakpointKey].matches) {
        // Found a matching breakpoint. Update the active breakpoint.
        activeBreakpoint = breakpointKey;
      }

      return {
        ...result,
        [breakpointKey]: breakpointToMediaQueryMap[breakpointKey].matches,
      };
    }, {} as BreakpointMatches);

    return {
      // Viewport breakpoints store does not require loading because viewport information is immediately ready when
      // it is initialized.
      isLoading: false,
      ...breakpointMatches,
      activeBreakpoint,
      isMobile: isMobileBreakpoint(activeBreakpoint),
      setActiveBreakpoint: (breakpoint) =>
        set(() => {
          return setActiveBreakpoint(breakpoints, breakpoint);
        }),
    };
  });
}

const BreakpointsContext = createContext<StoreApi<BreakpointsStore> | undefined>(undefined);

/**
 * Context provider that stores information about the responsive state of the chrono-ui breakpoints with respect to the
 * viewport, so that they can be used across many components without introducing performance issues associated with
 * making a huge number of `matchMedia` queries.
 *
 * This component is automatically provided to consumers via the `ThemeProvider` and SHOULD NOT be used
 * directly in production code. The provider is intentionally intended to be used once near the top of an application to
 * avoid performance issues, so using it throughout would defeat the purpose.
 *
 * When testing components that rely on the responsive utilities, you may need to wrap the components in a
 * `ThemeProvider` within your tests to ensure they work properly.
 */
export function ViewportBreakpointsProvider({ children }: ViewportBreakpointProviderProps) {
  const {
    theme: { breakpoints },
  } = useTheme();

  // Generate a mapping of breakpoints to MediaQueryList objects from the `matchMedia` api. These will enable us to
  // determine the responsive state of the application.
  const breakpointToMediaQueryMap = useMemo(() => {
    return getBreakpointToMediaQueryListMap(breakpoints);
  }, [breakpoints]);

  // Putting the store in context using a ref per recommendation from zustand docs.
  // https://zustand.docs.pmnd.rs/guides/initialize-state-with-props#wrapping-the-context-provider
  const storeRef = useRef<StoreApi<BreakpointsStore>>();
  if (!storeRef.current) {
    // Initialize the store using the current state of the MediaQueryList objects.
    storeRef.current = initializeBreakpointStore(breakpoints, breakpointToMediaQueryMap);
  }

  // Set up change event listeners for the MediaQueryList objects. This enables us to update the store when the page
  // is resized and the active breakpoint changes. We do this inside a useEffect, so that we can ensure those event
  // listeners get cleaned up when the ViewportBreakpointsProvider is unmounted.
  useLayoutEffect(() => {
    // If we have not yet initialized the zustand store, throw an error. It should be initialized by the time this hook
    // runs, but TS has trouble understanding that.
    if (!storeRef.current) {
      throw new Error('Something went wrong initializing the ViewportBreakpointsProvider.');
    }

    const setActiveBreakpoint = storeRef.current.getState().setActiveBreakpoint;

    // We need to store a list of the callback functions, so we can remove them in the cleanup.
    const callbackFns = breakpoints.keys.map((breakpointKey) => {
      const callback = (e: MediaQueryListEvent) => {
        // Updating only on the active breakpoint changing to avoid too many event triggers on transitions
        // (one for the prior breakpoint going "false" and one for the new breakpoint going "true") that lead to
        // too many re-renders and odd behavior. Only a single breakpoint is ever active, so this is simpler and avoids
        // race conditions.
        if (e.matches) {
          setActiveBreakpoint(breakpointKey);
        }
      };

      breakpointToMediaQueryMap[breakpointKey].addEventListener('change', callback);

      return callback;
    });

    return () => {
      // Clean up the event listeners on unmount, so we do not end up with memory leaks.
      breakpoints.keys.map((breakpointKey, i) => {
        breakpointToMediaQueryMap[breakpointKey].removeEventListener('change', callbackFns[i]!);
      });
    };
  }, [breakpointToMediaQueryMap, breakpoints.keys]);

  return <BreakpointsContext.Provider value={storeRef.current}>{children}</BreakpointsContext.Provider>;
}

/**
 * Returns the zustand store used to keep track of the responsive state of breakpoints across the application. This
 * hook SHOULD NOT be used directly by consumers. See hooks in `breakpointUtils.tsx` for consumer-friendly ways to
 * retrieve information from the store.
 */
export const useViewportBreakpointsStore = () => {
  const store = useContext(BreakpointsContext);

  if (!store) {
    if (process.env.NODE_ENV === 'test') {
      // In test environments, we will return a simple fallback store. This minimizes the need for developers to wrap
      // components they are testing in `ThemeProvider` in use cases where they are not trying to test responsive
      // behavior.
      return getFallbackBreakpointsStore();
    } else {
      // In all other environments (development, production), throw an error if the context provider is missing because
      // the responsive behavior will break without it. The provider automatically comes with `ThemeProvider`, which is
      // used near the very top of our application code, so it should be very rare for this to be triggered.
      throw new Error('Missing ViewportBreakpointsProvider');
    }
  }

  return store;
};
